import React, { useEffect, useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Avatar,
  Tooltip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  People,
  Business,
  AccountCircle,
  Logout,
  EventNote,
  Class,
  BookOnline,
  Receipt,
  Reviews,
  Inventory,
  LocalOffer,
  CardGiftcard,
  Campaign,
  Notifications,
  Map,
  CalendarToday,
  VpnKey,
  ManageAccounts,
  Group,
  Add,
  Person,
  CalendarMonth,
  BugReport,
  FitnessCenter,
  Assignment,
  ShoppingBag,
  MeetingRoom,
  History,
  Science,
  HourglassTop,
  AccountBalance,
  Storefront,
  ReceiptLong,
  FilterList,
  Search,
  Lightbulb,
  PushPin,
  PushPinOutlined,
  ExpandMore,
  ChevronRight,
  LockOutlined,
  EmojiEvents,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { colors } from '../theme/theme';
import { ENV_CONFIG } from '../config/env';
import LocationSwitcher from './LocationSwitcher';
import TestInstanceBadge from './TestInstanceBadge';
import FeatureRequestsFab from './featureRequests/FeatureRequestsFab';
import { STUDIO_SCOPED_ROLES, isOwnerRole } from '../utils/roles';
import { useMyLocations, anyLocationHas } from '../hooks/useMyLocations';
import { getFeatureLock } from './FeatureLocked';

const drawerWidth = 240;

interface LayoutProps {
  children: React.ReactNode;
}

type NavSectionId =
  | 'overview'
  | 'bookings'
  | 'schedule'
  | 'members'
  | 'commerce'
  | 'finance'
  | 'marketing'
  | 'platform'
  | 'studio';

interface NavigationItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  section: NavSectionId;
  permission?: string;
  studioOnly?: boolean;
  superAdminOnly?: boolean;
}

// Sidebar sections, top to bottom. Pinned pages render in their own section
// above all of these. A section with no visible items for the role is hidden.
const navSections: { id: NavSectionId; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'bookings', label: 'Bookings' },
  { id: 'schedule', label: 'Schedule' },
  { id: 'members', label: 'Members' },
  { id: 'commerce', label: 'Commerce' },
  { id: 'finance', label: 'Finance' },
  { id: 'marketing', label: 'Marketing' },
  { id: 'platform', label: 'Platform' },
  { id: 'studio', label: 'Studio' },
];

// Order within a section is the order here.
const navigationItems: NavigationItem[] = [
  { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard', section: 'overview' },
  // The studio's own profile, settings, holidays and announcements — right
  // under Dashboard. (Path kept: it redirects to /studios/:id.)
  { text: 'Manage Studio', icon: <ManageAccounts />, path: '/studio-profile', section: 'overview', studioOnly: true },

  { text: 'Bookings', icon: <BookOnline />, path: '/bookings', section: 'bookings', permission: 'bookings.view' },
  { text: 'Active Classes', icon: <CalendarToday />, path: '/active-classes', section: 'bookings', permission: 'classes.view' },
  { text: 'Session Waitlist', icon: <HourglassTop />, path: '/session-waitlist', section: 'bookings', studioOnly: true },

  { text: 'Classes', icon: <Class />, path: '/classes', section: 'schedule', permission: 'classes.view' },
  { text: 'Sessions', icon: <CalendarMonth />, path: '/studio-sessions', section: 'schedule', studioOnly: true },
  { text: 'Events', icon: <EventNote />, path: '/events', section: 'schedule', permission: 'events.view' },
  { text: 'Rooms & Layouts', icon: <MeetingRoom />, path: '/rooms', section: 'schedule' }, // studio roles: hidden unless studioPermissions.enableSeatSelection

  { text: 'Users', icon: <People />, path: '/users', section: 'members', permission: 'users.view' },
  { text: 'Users Map', icon: <Map />, path: '/users-map', section: 'members', permission: 'users.view' },
  { text: 'Customers', icon: <People />, path: '/studio-customers', section: 'members', studioOnly: true },
  { text: 'Health Forms', icon: <Assignment />, path: '/health-forms', section: 'members', studioOnly: true },

  { text: 'Packs', icon: <Inventory />, path: '/packs', section: 'commerce', permission: 'packs.view' },
  { text: 'Pack Subscriptions', icon: <Inventory />, path: '/pack-subscriptions', section: 'commerce', permission: 'packs.view' },
  { text: 'Merchandise', icon: <ShoppingBag />, path: '/merchandise', section: 'commerce' },
  { text: 'Coupons', icon: <LocalOffer />, path: '/coupons', section: 'commerce', permission: 'promo_codes.view' },
  { text: 'Promo Codes', icon: <LocalOffer />, path: '/promo-codes', section: 'commerce', permission: 'promo_codes.view' },

  { text: 'Transactions', icon: <Receipt />, path: '/transactions', section: 'finance', permission: 'transactions.view' },
  { text: 'Invoices', icon: <ReceiptLong />, path: '/invoice-generator', section: 'finance' },
  { text: 'Studio Payments', icon: <AccountBalance />, path: '/studio-payments', section: 'finance', superAdminOnly: true },
  { text: 'Payouts', icon: <AccountBalance />, path: '/payouts', section: 'finance' },

  { text: 'Engagement', icon: <Notifications />, path: '/notifications', section: 'marketing', permission: 'notifications.view' }, // push + WhatsApp (WhatsApp only for super admins / enabled studios)
  { text: 'In-App Banners', icon: <Campaign />, path: '/advertisements', section: 'marketing', permission: 'advertisements.view' },
  { text: 'Reviews', icon: <Reviews />, path: '/reviews', section: 'marketing' },
  { text: 'Leads & Inquiries', icon: <FilterList />, path: '/leads-inquiries', section: 'marketing', superAdminOnly: true },
  { text: 'Referrals', icon: <CardGiftcard />, path: '/referrals', section: 'marketing' }, // studio roles: hidden unless studioPermissions.enableReferrals
  { text: 'Milestones', icon: <EmojiEvents />, path: '/milestones', section: 'marketing' }, // studio roles: hidden unless studioPermissions.enableMilestones

  { text: 'Studios', icon: <Business />, path: '/studios', section: 'platform', permission: 'studios.view' },
  { text: 'Brands', icon: <Storefront />, path: '/brands', section: 'platform', superAdminOnly: true },
  { text: 'Studio Enquiries', icon: <FitnessCenter />, path: '/studio-enquiries', section: 'platform', superAdminOnly: true },
  { text: 'Whitelabel', icon: <VpnKey />, path: '/whitelabel', section: 'platform' },
  { text: 'Partner API Logs', icon: <BugReport />, path: '/partner-api-logs', section: 'platform', superAdminOnly: true },
  { text: 'Studio Ops', icon: <FitnessCenter />, path: '/studio-ops', section: 'platform', superAdminOnly: true },
  { text: 'QA Dashboard', icon: <Science />, path: '/qa-dashboard', section: 'platform', superAdminOnly: true },
  { text: 'Audit Logs', icon: <History />, path: '/audit-logs', section: 'platform' },
  // Internal admin/super_admin triage — separate page, separate path.
  { text: 'Feature Requests', icon: <Lightbulb />, path: '/admin/feature-requests', section: 'platform' },

  { text: 'Staff', icon: <Group />, path: '/staff', section: 'studio', studioOnly: true },
  { text: 'Instructors', icon: <Person />, path: '/instructors', section: 'studio', studioOnly: true },
  // Studio OWNER only — manager/instructor tokens are excluded below (the
  // backend's StudioAdminOnlyGuard 403s them on every feature request route).
  { text: 'Feature Requests', icon: <Lightbulb />, path: '/feature-requests', section: 'studio', studioOnly: true },
];

// Pinned pages live in this browser only (no backend), keyed per admin so two
// people sharing a machine keep their own pins.
const MAX_PINS = 5;
const pinsStorageKey = (adminId?: string) => `sidebar_pins_${adminId || 'default'}`;

const readPins = (key: string): string[] => {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter((p) => typeof p === 'string').slice(0, MAX_PINS)
      : [];
  } catch {
    return [];
  }
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  // Every location this account holds, for nav gating. A brand-level flag is
  // "on somewhere in this brand", not "on at the branch I signed in as".
  const { locations: myLocations } = useMyLocations();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { admin, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const pinsKey = pinsStorageKey((admin as any)?._id || admin?.email);
  const [pinnedPaths, setPinnedPaths] = useState<string[]>(() => readPins(pinsKey));
  useEffect(() => {
    setPinnedPaths(readPins(pinsKey));
  }, [pinsKey]);
  const togglePin = (path: string) => {
    const next = pinnedPaths.includes(path)
      ? pinnedPaths.filter((p) => p !== path)
      : pinnedPaths.length >= MAX_PINS
        ? pinnedPaths
        : [...pinnedPaths, path];
    setPinnedPaths(next);
    try {
      localStorage.setItem(pinsKey, JSON.stringify(next));
    } catch {
      /* storage unavailable — pins just won't survive a reload */
    }
  };

  // Collapsed sections. Not persisted: every section starts open on each load.
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(() => new Set());
  const toggleSection = (id: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Logout asks first — a mis-click on the account menu should not end the
  // session.
  const [confirmLogoutOpen, setConfirmLogoutOpen] = useState(false);
  const handleLogout = () => {
    handleMenuClose();
    setConfirmLogoutOpen(true);
  };
  const confirmLogout = async () => {
    setConfirmLogoutOpen(false);
    await logout();
  };
  // Same reach as the side-panel link: studio owners and managers (an
  // instructor's panel never offered the studio profile).
  const isStudioAccount =
    STUDIO_SCOPED_ROLES.includes(admin?.role || '') && admin?.role !== 'instructor';

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileOpen(false);
    setSearchQuery('');
  };

  const handleCreateNavigation = (path: string) => {
    navigate(path, { state: { openCreate: true } });
    setMobileOpen(false);
  };

  const filteredNavigationItems = navigationItems.filter(item => {
    const role = admin?.role || '';
    const isStudioRole = STUDIO_SCOPED_ROLES.includes(role);

    // Transactions, Invoices + Payouts are for studio OWNERS + admins only — not managers/instructors.
    if ((item.path === '/transactions' || item.path === '/invoice-generator' || item.path === '/payouts') && (role === 'manager' || role === 'instructor')) return false;

    // Feature Requests is a work-in-progress feature — only enabled on the Sankalp
    // test instance (same ENV_CONFIG.IS_TEST_INSTANCE check as the TEST INSTANCE
    // badge), never on the neelniketan production panel.
    if ((item.path === '/feature-requests' || item.path === '/admin/feature-requests') && !ENV_CONFIG.IS_TEST_INSTANCE) return false;

    // Feature Requests (studio side) is OWNER only (studio / studio_owner /
    // brand_owner, same as the FAB) — the backend rejects manager/instructor tokens outright.
    if (item.path === '/feature-requests' && !isOwnerRole(role)) return false;

    // Super-admin payouts live in Studio Payments — not this studio-scoped page.
    if (item.path === '/payouts' && role === 'super_admin') return false;

    // superAdminOnly items are hidden from everyone except super_admin
    if (item.superAdminOnly && role !== 'super_admin') return false;

    // studioOnly items are hidden from non-studio roles
    if (item.studioOnly && !isStudioRole) return false;

    // Users + Users Map only make sense for a studio that has its own app.
    // Studios without a whitelabel app don't get an app-based user base, so hide them.
    if (isStudioRole && (item.path === '/users' || item.path === '/users-map')) {
      // "Has an app" — the location's own app source, or any location of its
      // brand (branches inherit the brand's). NOT "has a brand": after the
      // migration every studio has one, which showed these tabs to studios
      // with no app of their own.
      const studio = admin?.studio as any;
      const hasWhitelabelApp =
        !!studio?.whitelabelConfig?.appSource ||
        anyLocationHas(myLocations, 'whitelabelConfig.appSource', (v) => Boolean(v));
      // A studio with no app but its own booking website (e.g. Vyāna on
      // vyana.book.sweatsocial.club) has members of its own too. It must not
      // need an appSource to see them — that value is checked against fixed
      // lists, and an unknown one fails every booking with a 500.
      const wl = studio?.whitelabelConfig ?? {};
      const hasBookingWebsite = wl.enableBookingWebsite === true || !!wl.bookingSlug || !!wl.bookingDomain;
      if (!hasWhitelabelApp && !hasBookingWebsite) return false;
    }

    // Rooms & Layouts is only meaningful for a studio that books by seat.
    // Everywhere else a session is booked by headcount, so a room builder is
    // noise the studio can't act on — and the backend would 403 every call
    // anyway (seat-layouts.service assertEnabled).
    //
    // Super admins keep the tab: the page has its own studio picker, and they
    // are the ones who turn `enableSeatSelection` on in the first place.
    if (isStudioRole && item.path === '/rooms') {
      // "Any location has it on" — `admin.studio` is only the default branch
      // while viewing All, so gating on it alone made the tab blink in and
      // out as the owner switched. See anyLocationHas.
      const seatSelectionEnabled =
        (admin?.studio as any)?.studioPermissions?.enableSeatSelection === true ||
        anyLocationHas(myLocations, 'studioPermissions.enableSeatSelection');
      if (!seatSelectionEnabled) return false;
    }

    // Referrals is hidden until the studio is onboarded onto it — the backend
    // 403s every call otherwise (referrals.service assertEnabled), so showing
    // the tab would only offer a page that cannot load.
    if (isStudioRole && item.path === '/referrals') {
      const referralsEnabled =
        (admin?.studio as any)?.studioPermissions?.enableReferrals === true ||
        anyLocationHas(myLocations, 'studioPermissions.enableReferrals');
      if (!referralsEnabled) return false;
    }

    if (isStudioRole && item.path === '/milestones') {
      const milestonesEnabled =
        (admin?.studio as any)?.studioPermissions?.enableMilestones === true ||
        anyLocationHas(myLocations, 'studioPermissions.enableMilestones');
      if (!milestonesEnabled) return false;
    }

    // Advertisements only exist inside a studio's OWN app. A studio without a
    // whitelabel app has nowhere for one to render — the SweatSocial app shows
    // banners, which are a SweatSocial placement and not theirs to create — so
    // the tab would offer a page whose output nobody could ever see.
    //
    // Super admins keep it: they manage SweatSocial's own banners from here.
    if (isStudioRole && item.path === '/advertisements') {
      const appSource =
        (admin?.studio as any)?.whitelabelConfig?.appSource ||
        (anyLocationHas(myLocations, 'whitelabelConfig.appSource', (v) => Boolean(v))
          ? 'brand'
          : undefined);
      if (!appSource) return false;
    }

    // Studio roles see limited navigation
    if (isStudioRole) {
      // Instructors: base set is dashboard + classes
      // When studio has instructorViewAllBookings enabled, also show bookings + sessions
      if (role === 'instructor') {
        const instructorViewAllBookings =
          (admin?.studio as any)?.whitelabelConfig?.instructorViewAllBookings === true;
        const instructorPaths = ['/dashboard', '/classes'];
        if (instructorViewAllBookings) {
          instructorPaths.push('/bookings', '/studio-sessions');
        }
        return instructorPaths.includes(item.path);
      }
      // Studio owners and managers see broader set
      const studioAllowedPaths = [
        '/dashboard',
        '/classes',
        '/studio-sessions',
        '/session-waitlist',
        '/events',
        '/bookings',
        '/transactions',
        '/invoice-generator',
        '/payouts',
        '/packs',
        '/pack-subscriptions',
        '/coupons',
        '/referrals',
        '/milestones',
        '/users',
        '/health-forms',
        '/studio-profile',
        '/staff',
        '/instructors',
        '/notifications',
        '/users-map',
        '/advertisements',
        '/merchandise',
        '/rooms',
        '/feature-requests',
      ];
      
      return studioAllowedPaths.includes(item.path);
    }

    // Admins see items based on permissions
    return !item.permission || hasPermission(item.permission);
  });

  // "/studio-profile" immediately redirects to "/studios/:studioId"
  // (see StudioProfilePage in App.tsx), so location.pathname never
  // equals item.path for this one — match the redirected URL too.
  const studioProfileId = (admin?.studio as any)?._id;
  const isItemActive = (item: NavigationItem) =>
    location.pathname === item.path ||
    (item.path === '/studio-profile' &&
      !!studioProfileId &&
      location.pathname === `/studios/${studioProfileId}`);

  const query = searchQuery.trim().toLowerCase();
  const visibleItems = query
    ? filteredNavigationItems.filter((item) => item.text.toLowerCase().includes(query))
    : filteredNavigationItems;

  // Pins the current role can still see, in pin order. A pinned page shows
  // only under Pinned and returns to its own section once unpinned. While
  // searching, Pinned is hidden and every match shows in its own section.
  const pinnedItems = query
    ? []
    : pinnedPaths
        .map((p) => filteredNavigationItems.find((i) => i.path === p))
        .filter((i): i is NavigationItem => !!i);
  const isShownAsPinned = (item: NavigationItem) =>
    pinnedItems.some((p) => p.path === item.path);

  // The section of the page in use (detail pages like /classes/:id count for
  // their list page; a pinned page's section is Pinned). That section can't
  // be collapsed until the user moves to a page in another section.
  const activeItem =
    filteredNavigationItems.find(isItemActive) ||
    filteredNavigationItems.find(
      (i) => i.path !== '/dashboard' && location.pathname.startsWith(`${i.path}/`),
    );
  const activeSection = activeItem
    ? isShownAsPinned(activeItem)
      ? 'pinned'
      : activeItem.section
    : undefined;

  const navGroups: { id: string; label: string; items: NavigationItem[] }[] = [];
  navGroups.push({ id: 'pinned', label: 'Pinned', items: pinnedItems });
  navSections.forEach((section) => {
    navGroups.push({
      id: section.id,
      label: section.label,
      items: visibleItems.filter((i) => i.section === section.id && !isShownAsPinned(i)),
    });
  });

  const renderNavItem = (item: NavigationItem, keyPrefix: string) => {
    const isActive = isItemActive(item);
    const isPinned = pinnedPaths.includes(item.path);
    const canPin = item.path !== '/dashboard'; // always first in Overview anyway
    const pinLimitReached = !isPinned && pinnedPaths.length >= MAX_PINS;
    // Not set up for this studio yet: dimmed but still clickable — the page
    // explains how to get it (see FeatureGate).
    const isLocked = !!getFeatureLock(admin, item.path);
    const showCreateShortcut = false; // removed create shortcuts from sidebar

    return (
      <ListItem key={`${keyPrefix}${item.path}`} disablePadding sx={{ px: 2 }}>
        <ListItemButton
          onClick={() => handleNavigation(item.path)}
          selected={isActive}
          sx={{
            borderRadius: 2,
            mb: 0.5,
            '&.Mui-selected': {
              backgroundColor: 'rgba(244, 143, 180, 0.18)',
              color: '#f48fb4',
              borderLeft: '3px solid #f48fb4',
              '& .MuiListItemIcon-root': {
                color: '#f48fb4',
              },
              '&:hover': {
                backgroundColor: 'rgba(244, 143, 180, 0.25)',
              },
            },
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
            },
            '&:hover .pin-btn, & .pin-btn:focus-visible': {
              opacity: 1,
            },
            ...(isLocked && !isActive ? { opacity: 0.5 } : {}),
          }}
        >
          <ListItemIcon
            sx={{
              color: isActive ? '#f48fb4' : 'rgba(255, 255, 255, 0.7)',
              minWidth: 34,
              '& .MuiSvgIcon-root': { fontSize: 20 },
            }}
          >
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={
              isLocked ? (
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  {item.text}
                  <LockOutlined sx={{ fontSize: 14, color: 'rgba(255, 255, 255, 0.55)' }} />
                </Box>
              ) : (
                item.text
              )
            }
            sx={{
              color: isActive ? '#f48fb4' : 'rgba(255, 255, 255, 0.9)',
              '& .MuiTypography-root': {
                fontSize: '0.875rem',
                fontWeight: isActive ? 600 : 400,
              }
            }}
          />
          {canPin && !isLocked && (
            <Tooltip
              title={
                isPinned
                  ? 'Unpin'
                  : pinLimitReached
                    ? `You can pin up to ${MAX_PINS} pages`
                    : 'Pin to sidebar'
              }
              placement="right"
            >
              <span>
                <IconButton
                  className="pin-btn"
                  size="small"
                  aria-label={isPinned ? `Unpin ${item.text}` : `Pin ${item.text}`}
                  disabled={pinLimitReached}
                  onClick={(e) => {
                    e.stopPropagation();
                    togglePin(item.path);
                  }}
                  sx={{
                    p: 0.4,
                    color: isPinned ? '#f48fb4' : 'rgba(255,255,255,0.55)',
                    // Hover-revealed on desktop; always faintly visible on
                    // touch screens, which have no hover.
                    opacity: { xs: isPinned ? 0.9 : 0.45, sm: isPinned ? 0.9 : 0 },
                    '&.Mui-disabled': { color: 'rgba(255,255,255,0.25)' },
                    '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.12)' },
                  }}
                >
                  {isPinned ? (
                    <PushPin sx={{ fontSize: 16 }} />
                  ) : (
                    <PushPinOutlined sx={{ fontSize: 16 }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {showCreateShortcut && (
            <Tooltip title={`Create ${item.text.replace(/s$/, '')}`} placement="right">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCreateNavigation(item.path);
                }}
                sx={{
                  color: 'rgba(255,255,255,0.55)',
                  p: 0.4,
                  borderRadius: 1,
                  '&:hover': {
                    color: '#fff',
                    backgroundColor: 'rgba(255,255,255,0.12)',
                  },
                }}
              >
                <Add fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </ListItemButton>
      </ListItem>
    );
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo Section */}
      <Box sx={{ p: 3, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            component="img"
            src="/logo.png"
            alt="Sweat Social Logo"
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: 'white',
              padding: '4px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            }}
          />
          <Typography 
            variant="h5" 
            component="div" 
            sx={{ 
              color: '#ffffff', 
              fontWeight: 700,
              fontSize: '1.5rem',
              letterSpacing: '0.5px'
            }}
          >
            Sweat Social
          </Typography>
        </Box>
      </Box>
      
      {/* Search Bar */}
      <Box sx={{ px: 2, pt: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search menu..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: 'rgba(255, 255, 255, 0.6)' }} fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              color: '#ffffff',
              backgroundColor: 'rgba(255, 255, 255, 0.06)',
              borderRadius: 2,
              '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.15)',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.3)',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#f48fb4',
              },
            },
            '& .MuiInputBase-input::placeholder': {
              color: 'rgba(255, 255, 255, 0.5)',
              opacity: 1,
            },
          }}
        />
      </Box>

      {/* Navigation Menu */}
      <Box sx={{ flex: 1, py: 2 }}>
        {navGroups.map((group) => {
          if (group.items.length === 0) return null;
          // While searching every matching section is shown open.
          const locked = !!query || group.id === activeSection;
          const open = locked || !collapsedSections.has(group.id);
          return (
            <Box key={group.id} sx={{ mb: 0.5 }}>
              <Box
                component="button"
                type="button"
                onClick={() => !locked && toggleSection(group.id)}
                aria-expanded={open}
                disabled={locked}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  px: 3,
                  pt: 1.5,
                  pb: 0.5,
                  border: 0,
                  background: 'none',
                  font: 'inherit',
                  cursor: locked ? 'default' : 'pointer',
                  color: 'rgba(255, 255, 255, 0.5)',
                  '&:hover:not(:disabled)': { color: 'rgba(255, 255, 255, 0.85)' },
                }}
              >
                <Typography
                  component="span"
                  sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' }}
                >
                  {group.label}
                </Typography>
                {!locked &&
                  (open ? <ExpandMore sx={{ fontSize: 16 }} /> : <ChevronRight sx={{ fontSize: 16 }} />)}
              </Box>
              {open && (
                <List disablePadding>
                  {group.items.map((item) => renderNavItem(item, group.id === 'pinned' ? 'pin:' : ''))}
                </List>
              )}
            </Box>
          );
        })}
        {query && visibleItems.length === 0 && (
          <Typography variant="body2" sx={{ px: 3, py: 2, color: 'rgba(255, 255, 255, 0.5)' }}>
            No pages match "{searchQuery.trim()}"
          </Typography>
        )}
      </Box>

      {/* User Info at Bottom */}
      <Box sx={{ p: 2, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: '#e96d9a', width: 32, height: 32 }}>
            {admin?.name?.charAt(0) || admin?.email?.charAt(0) || 'A'}
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 500 }}>
              {admin?.name || 'Admin'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
              {admin?.role?.replace('_', ' ').toUpperCase() || 'ADMIN'}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Top App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          backgroundColor: '#ffffff',
          color: '#333333',
          boxShadow: 'none',
          borderBottom: `1px solid ${colors.divider}`,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' }, color: '#333333' }}
          >
            <MenuIcon />
          </IconButton>
          {/* Logo visible on mobile when sidebar is hidden */}
          <Box
            component="img"
            src="/logo.png"
            alt="Sweat Social"
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              backgroundColor: colors.sidebarBg,
              padding: '3px',
              mr: 1.5,
              display: { xs: 'block', sm: 'none' },
            }}
          />
          <Typography variant="h6" noWrap component="div" sx={{ color: '#333333', fontWeight: 600 }}>
            {(() => {
              const studioProfileId = (admin?.studio as any)?._id;
              if (studioProfileId && location.pathname === `/studios/${studioProfileId}`) {
                return 'Manage Studio';
              }
              return navigationItems.find(item => item.path === location.pathname)?.text || 'Dashboard';
            })()}
          </Typography>

          <Box sx={{ ml: 2 }}>
            <TestInstanceBadge />
          </Box>

          {/* Only renders for someone who holds more than one location. */}
          <Box sx={{ ml: 2 }}>
            <LocationSwitcher />
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={{ color: '#666666' }}>
              {admin?.name || admin?.email}
            </Typography>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenuOpen}
              sx={{ color: '#333333' }}
            >
              <AccountCircle />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              {isStudioAccount && (
                <MenuItem
                  onClick={() => {
                    handleMenuClose();
                    handleNavigation('/studio-profile');
                  }}
                >
                  <ListItemIcon>
                    <ManageAccounts fontSize="small" />
                  </ListItemIcon>
                  Manage Studio
                </MenuItem>
              )}
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <Logout fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
            <Dialog open={confirmLogoutOpen} onClose={() => setConfirmLogoutOpen(false)} maxWidth="xs" fullWidth>
              <DialogTitle>Log out?</DialogTitle>
              <DialogContent>
                <Typography variant="body2">Are you sure you want to logout?</Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setConfirmLogoutOpen(false)}>Cancel</Button>
                <Button color="error" variant="contained" onClick={confirmLogout}>
                  Logout
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        </Toolbar>
      </AppBar>
      
      {/* Sidebar */}
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              backgroundColor: colors.sidebarBg,
              borderRadius: 0,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              backgroundColor: colors.sidebarBg,
              borderRight: 'none',
              borderRadius: 0,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      
      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: colors.background,
          minHeight: '100vh',
        }}
      >
        <Toolbar />
        {children}
      </Box>

      <FeatureRequestsFab />
    </Box>
  );
};

export default Layout;